import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { parseStatsCpuData } from "views/Statistics/System/SysStatsCpu/api";
import { ChartDownsampled } from "views/Statistics/System/SysStatsCpu/Chart";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import Legend from "common/graphs/Legend";
import {
  PERIOD_DASHBOARD_PERIOD_MAP,
  getChartFields,
  getLegendFields,
} from "./utils";
import useDashboardData from "./useDashboardData";
import Loading from "common/Loading";
import { atBqnTarget } from "common/api";
import { PERIOD_TO_LINES } from "common/constants";

const Wrapper = styled.div`
  width: 100%;
`;

const ChartContainer = styled.div`
  width: calc(100% + 20px);
  min-height: 200px;
  margin-top: -10px;
  margin-left: -10px;
  margin-right: -10px;

  svg {
    width: 100%;
    height: 200px;
  }
  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const LegendContainer = styled.div`
  width: 100%;
  padding: 0px 55px 0px 55px;
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  margin-top: 10px;
  margin-bottom: 20px;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: var(--text-primary);
`;

const getCPUUsageData = ({ mode, bqnId, period }) => {
  const periodRtx = PERIOD_DASHBOARD_PERIOD_MAP[period];
  return ifCl.run(
    atBqnTarget(
      `show statistics cpu idle lines ${PERIOD_TO_LINES[periodRtx]}`,
      mode,
      bqnId
    )
  );
};

function CPUUsage({ period, range }) {
  const [legendFieldsSet, setLegendFieldsSet] = useState(new Set());

  const colorsSet = login.isTheme("light") ? colors : colorsDark;

  // The range of the dashboard is the clock of the chart, which is asked for
  // again with each new one of them, and the one the samples are taken in.
  const { data: dataRange, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId, range }) =>
      getCPUUsageData({ mode, bqnId, period }).then((response) => {
        const { from, to } = range;

        return { data: parseStatsCpuData(response, from, to), range };
      }),
    onError: "Error fetching CPU usage data:",
    deps: [period],
    range,
  });

  const { data, range: rangeDrawn } = dataRange || {};
  const { items, fields } = data || {};

  const chartFields = useMemo(
    () => (fields === undefined ? [] : getChartFields(fields, colorsSet)),
    [fields, colorsSet]
  );
  const legendFields = useMemo(
    () => getLegendFields(chartFields),
    [chartFields]
  );

  const legendGiven = legendFields.map(({ name }) => name).join("\u0000");

  useEffect(() => {
    if (legendFields.length > 0) {
      setLegendFieldsSet(new Set(legendFields.map(({ name }) => name)));
    }
  }, [legendGiven]);

  // An item of the legend that is not selected takes out every field it
  // names, which is more than one for the one that groups the rest of them.
  const displayFields = useMemo(
    () => chartFields.filter(({ legend }) => legendFieldsSet.has(legend)),
    [chartFields, legendFieldsSet]
  );

  const transformedItems = useMemo(() => {
    if (!items || !fields) return [];
    return items.map((item) => ({
      time: item.time,
      ...Object.fromEntries(
        fields.map((f) => [f, Number.isFinite(item[f]) ? 100 - item[f] : null])
      ),
    }));
  }, [items, fields]);

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!fields || fields.length === 0 || !items || items.length === 0) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <Subtitle>Showing Busy</Subtitle>
      <LegendContainer>
        <Legend
          fields={legendFields}
          value={legendFieldsSet}
          onChange={(filterSet) => setLegendFieldsSet(filterSet)}
        />
      </LegendContainer>
      <ChartContainer>
        <ChartDownsampled
          tooltipEnabled={false}
          fixedYRange={[0, 100]}
          items={transformedItems}
          fields={displayFields}
          range={rangeDrawn}
        />
      </ChartContainer>
    </Wrapper>
  );
}

export default React.memo(CPUUsage);
